import {
  Button,
  Col,
  Form,
  Modal,
  Popover,
  Row,
  Spin,
  message,
  Image,
  Tag,
  Tooltip,
  Space,
  Badge,
  Rate,
  Divider,
  Popconfirm,
} from 'antd';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import moment from 'moment';

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { ReactSVG } from 'react-svg';
import { TraceLogs } from '../Modals/TraceLogs';

import {
  DATE_BACKEND_FORMAT,
  DAYTIME_FORMAT,
  DAY_FORMAT,
  ORDER_STATUSES,
  ORDER_TYPE,
  ROLES,
  SPARQLE_DARK_BLUE,
  STEP_TYPE,
  TIME_FORMAT,
} from '../../constants';
import { format } from 'date-fns';

import './OrderDetail.scss';
import { imageMaker } from '../../utils/common';
import { doCancelOrder, doGetOrder, doUpdateOrder } from '../../modules/orders/store/thunk';
import DefaultMapbox from '../Mapbox/DefaultMapbox';
import Icon from '../Markers/icons/pickup';
import OrderForm from '../Forms/Orders/OrderForm';

import { selectAllLocations } from '../../modules/locations/stores/slice';
import BreadCrumb from '../../modules/breadcrumbs';
import HistoryLogs, { getDate } from '../Elements/HistoryLogs';
import DeliveryForm from '../Forms/Orders/DeliveryForm';
import DeliveryStatusForm from '../Forms/Orders/DeliveryStatusForm';
import PickupForm from '../Forms/Orders/PickupForm';

import './OrderDetail.scss';
import withPage from './withPage';
import { getLabel } from '../../modules/orders/api';
import ColloForm from '../Forms/Orders/ColloForm';
import { SupportNote } from './SupportNote';
import usePageView from '../../hooks/usePageView';
import { CloseOutlined, CodeTwoTone, MailOutlined } from '@ant-design/icons';
import ReportIssueForm from '../Forms/Issues/ReportIssueForm';
import { doGetIssue, doGetIssuesForOrder } from '../../modules/issues/stores/thunk';
import { selectIssueById, selectOrderActiveIssues } from '../../modules/issues/stores/slice';
import OrderIssues from '../Elements/OrderIssuesPopover';
import { clearState } from '../../modules/orders/store/slice';
import IssueChat from './Issues/IssueChat';
import { BadgeEuro, SquareCode } from 'lucide-react';

const typePage = {
  order: {
    type: 'orderInfo',
    title: 'Edit Order Info',
  },
  pickup: {
    type: 'pickupInfo',
    title: 'Edit Pickup',
  },
  delivery: {
    type: 'deliveryInfo',
    title: 'Edit Delivery',
  },
  deliveryStatus: {
    type: 'deliveryStatusInfo',
    title: 'Edit Delivery Status',
  },
  collo: {
    type: 'collo',
    title: 'Edit Order Dimensions',
  },
};

const OrderDetailPage = ({ id, params }) => {
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.auth.user.role);
  const user = useSelector((state) => state.auth.user);

  const isAdmin = useSelector((state) => state.auth.user.isAdmin);
  const isAdminOrMerchant = [ROLES.ADMIN, ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole);

  const order = useSelector((state) => state.order.orderDetail);

  const isFetchingOrder = useSelector((state) => state.order.isFetching);
  const isFetchingCompany = useSelector((state) => state.company.isFetching);
  const isFetchingLocation = useSelector((state) => state.location.isFetching);
  const status = useSelector((state) => state.order.status);
  const action = useSelector((state) => state.order.action);
  const locations = useSelector((state) => selectAllLocations(state));
  // const companies = useSelector((state) => selectAllCompanies(state));
  const issues = useSelector((state) => selectOrderActiveIssues(state));

  const [showLogsForOrder, setShowLogsForOrder] = useState(null);

  const isFetching = isFetchingOrder || isFetchingCompany || isFetchingLocation;
  const [messageApi, contextHolder] = message.useMessage();
  const breadCrumbs = [{ text: 'ORDERS', url: '/orders' }, { text: order?.orderId }];

  const [titleModal, setTitleModal] = useState('');
  const [actionPage, setActionPage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [types, setTypes] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [popoverIssuesVisible, setPopoverIssuesVisible] = useState(false);
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);
  const issue = useSelector((state) => selectIssueById(state, selectedIssueId));
  const [isIssueLoaded, setIsIssueLoaded] = useState(false);

  useEffect(() => {
    if (params?.issueId) {
      setSelectedIssueId(params.issueId);
      if (params.issueId) {
        dispatch(doGetIssue({ id: params.issueId })).then(() => {
          setIsIssueLoaded(true); // Set the issue as loaded when fetching completes
        });
      }
    }
  }, [params]);

  useEffect(() => {
    if (isIssueLoaded && issue) {
      setIsIssueModalOpen(true); // Only open modal after issue is loaded
    }
  }, [isIssueLoaded, issue]); // Runs when the issue state updates

  // Emit page view event
  usePageView(window.location.href);

  const logs = order?.logs || [];
  const statusDatetime = (status) => {
    const log = logs.find((l) => l.status === status);
    if (!log) return '';
    const d = getDate(log, order);
    return format(new Date(d), 'dd/MM/yyy HH:mm');
  };

  useEffect(() => {
    if (order && order.pickupAddressLat && order.deliveryAddressLat) {
      const newMarkers = [
        {
          id: 1,
          content: <img src={imageMaker(Icon())} />,
          latitude: Number(order.pickupAddressLat),
          longitude: Number(order.pickupAddressLng),
          offsetX: 0,
          offsetY: 0,
          className: 'order-marker',
        },
        {
          id: 2,
          content: <img src="/icons/dropoff.svg" />,
          latitude: Number(order.deliveryAddressLat),
          longitude: Number(order.deliveryAddressLng),
          offsetX: 17,
          offsetY: -10,
          className: 'order-marker',
        },
      ];
      setMarkers(newMarkers);
    }
  }, [order]);

  useEffect(() => {
    dispatch(clearState()); // Clear the state first, no no older order data is shown...
    dispatch(doGetOrder({ id }));
  }, []);

  useEffect(() => {
    if (order) {
      dispatch(doGetIssuesForOrder({ orderId: order?.id, status: 'active' }));
      dispatch(doGetIssuesForOrder({ orderId: order?.id, status: 'resolved' }));
    }
  }, [order, userRole]);

  useEffect(() => {
    if (action === 'getOne' && ['succeeded', 'failed'].includes(status) && !order) {
      if (!isFetchingOrder) {
        history.push('/orders');
      }
    } else if (order) {
      setTypes(order.types || []);
    }
  }, [action, status, order, isFetchingOrder]);

  useEffect(() => {
    if (action === 'update' && status === 'succeeded') {
      dispatch(doGetOrder({ id }));
      setIsModalOpen(false);
      setActionPage('');
    }
  }, [action, status]);

  const onClickEditInfo = () => {
    form.setFieldsValue({
      orderType: order.orderType,
      orderRef: order.orderRef,
      status: order.status,
      deliveryDate: order.status !== ORDER_STATUSES.EXPECTED ? dayjs(order.deliveryDate) : undefined,
      //load: order.load,
      createdBy: order.createdBy,
      name: order.name,
      description: order.description,
      region: order.region,
      // sizeLength: order.sizeLength ? Number(order.sizeLength.split(' ')[0]) : undefined,
      // sizeWidth: order.sizeWidth ? Number(order.sizeWidth.split(' ')[0]) : undefined,
      // sizeHeight: order.sizeHeight ? Number(order.sizeHeight.split(' ')[0]) : undefined,
      // weight: order.weight ? Number(order.weight.split(' ')[0]) : undefined,
      supportNote: order?.supportNote,
      attempt: order?.attempt,
    });
    if ([ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole)) {
      form.resetFields(['load', 'status', 'types', 'supportNote', 'attempt']);
      setTypes(undefined);
    }
    setActionPage(typePage.order.type);
    setTitleModal(typePage.order.title);
    setIsModalOpen(true);
  };

  const onClickEditPickup = () => {
    form.setFieldsValue({
      locationId: order.location?.id,
      pickupAddress: order.isReturn
        ? {
            address: order.pickupAddress,
            latLng: {
              lat: order.pickupAddressLat,
              lng: order.pickupAddressLng,
            },
          }
        : order.location.address,
      pickupAddressLng: order.isReturn ? undefined : order.location.addressLnt,
      pickupAddressLat: order.isReturn ? undefined : order.location.addressLat,

      pickupPhone: order.pickupPhone,

      pickupDuration: order.pickupDuration,
      pickupNote: order.pickupNote,
      pickupEmail: order.pickupEmail,
      pickupName: order.pickupName,
      companyId: order.location?.companyId,
      pickupTimeStart: order.pickupTimeStart,
      pickupTimeEnd: order.pickupTimeEnd,
      pickupTimeWindow: (order.pickupTimeStart || '') + ' - ' + (order.pickupTimeEnd || ''),
    });
    setActionPage(typePage.pickup.type);
    setTitleModal(typePage.pickup.title);
    setIsModalOpen(true);
  };

  const onClickEditDelivery = () => {
    const deliveryTimeStart = order.deliveryTimeStart ? dayjs(order.deliveryTimeStart, TIME_FORMAT) : null;
    const deliveryTimeEnd = order.deliveryTimeEnd ? dayjs(order.deliveryTimeEnd, TIME_FORMAT) : null;
    form.setFieldsValue({
      deliveryName: order.deliveryName,
      deliveryEmail: order.deliveryEmail,
      deliveryPhone: order.deliveryPhone,
      deliveryDuration: order.deliveryDuration,
      deliveryNote: order.deliveryNote,
      deliveryAddress: order.isReturn
        ? order.location.address
        : {
            address: order.deliveryAddress,
            latLng: {
              lat: order.deliveryAddressLat,
              lng: order.deliveryAddressLng,
            },
          },
      deliveryAddressLng: order.isReturn ? order.location.addressLng : order.deliveryAddressLat,
      deliveryAddressLat: order.isReturn ? order.location.addressLat : order.deliveryAddressLng,
      locationId: order.location?.id,
      companyId: order.location?.companyId,
      deliveryTimeWindow: order.isReturn
        ? (order.pickupTimeStart || '') + ' - ' + (order.pickupTimeEnd || '')
        : undefined,
      deliveryTimeStart: order.pickupTimeStart,
      deliveryTimeEnd: order.pickupTimeEnd,
      dateRange: [deliveryTimeStart, deliveryTimeEnd],
    });
    setActionPage(typePage.delivery.type);
    setTitleModal(typePage.delivery.title);
    setIsModalOpen(true);
  };

  const onClickEditDeliveryStatus = () => {
    form.setFieldsValue({
      packageWas: order.packageWas,
      neighbourAddress: order.neighbourAddress,
      failedReason: order.failedReason,
      riderNote: order.riderNote,
    });

    setActionPage(typePage.deliveryStatus.type);
    setTitleModal(typePage.deliveryStatus.title);
    setIsModalOpen(true);
  };

  const onClickReportIssue = () => {
    if (order) {
      setIsReportIssueModalOpen(true);
    }
  };

  const cancelLogsModal = () => {
    setIsLogsModalOpen(false);
  };

  const cancelReportIssueModal = () => {
    setIsReportIssueModalOpen(false);
  };

  const cancelIssueModal = () => {
    setIsIssueModalOpen(false);
  };

  const cancelModal = () => {
    setTypes(order.types || []);
    setIsModalOpen(false);
    setActionPage(undefined);
  };

  const onFinishForm = (values) => {
    let params = {};
    if (actionPage === typePage.order.type) {
      params = {
        width: values.width,
        length: values.length,
        height: values.height,
        weight: values.weight,
        orderType: values.orderType,
        orderRef: values.orderRef,
        region: values.region,
        deliveryDate: values.deliveryDate?.format(DATE_BACKEND_FORMAT) || undefined,
        load: values.load ? Number(values.load) : undefined,
        createdBy: values.createdBy,
        name: values.name,
        status: values.status,
        types: types,
        description: values.description,
        supportNote: values?.supportNote,
        attempt: values?.attempt,
      };
    }
    if (actionPage === typePage.pickup.type) {
      const location = locations.find((l) => l.id === values.locationId);
      const deliveryDate = order.deliveryDate ? dayjs(order.deliveryDate) : undefined;
      const day = deliveryDate?.format('dddd');
      params = {
        locationId: location?.locationId || undefined,
        pickupName: values.pickupName,
        pickupAddress: values.pickupAddress.addressObject,
        pickupAddressLat: values.pickupAddress.latLng?.lat ? String(values.pickupAddress.latLng?.lat) : undefined,
        pickupAddressLng: values.pickupAddress?.latLng?.lng ? String(values.pickupAddress?.latLng?.lng) : undefined,
        pickupPhone: values.pickupPhone,
        pickupNote: values.pickupNote,
        pickupDuration: values.pickupDuration ? Number(values.pickupDuration) : undefined,
        pickupTimeStart: values.pickupTimeStart || location?.[day?.toLowerCase() + 'From'] || undefined,
        pickupTimeEnd: values.pickupTimeEnd || location?.[day?.toLowerCase() + 'To'] || undefined,
        pickupEmail: values.pickupEmail,
      };
    }
    if (actionPage === typePage.delivery.type) {
      const location = locations.find((l) => l.id === values.locationId);
      const deliveryAddress = order.isReturn ? location.address : values.deliveryAddress;
      const deliveryTimeStart =
        values.dateRange?.length > 0 && values.dateRange[0] ? values.dateRange[0].format(TIME_FORMAT) : undefined;
      const deliveryTimeEnd =
        values.dateRange?.length > 1 && values.dateRange[1] ? values.dateRange[1].format(TIME_FORMAT) : undefined;
      params = {
        deliveryName: values.deliveryName,
        deliveryEmail: values.deliveryEmail,
        deliveryPhone: values.deliveryPhone,
        deliveryDuration: values.deliveryDuration ? Number(values.deliveryDuration) : undefined,
        deliveryNote: values.deliveryNote,
        deliveryAddress: deliveryAddress.addressObject,
        deliveryAddressLat: values.deliveryAddressLat ? String(values.deliveryAddressLat) : undefined,
        deliveryAddressLng: values.deliveryAddressLng ? String(values.deliveryAddressLng) : undefined,
        deliveryTimeStart,
        deliveryTimeEnd,
      };
    }

    if (actionPage === typePage.deliveryStatus.type) {
      params = {
        packageWas: values.packageWas,
        neighbourAddress: values.neighbourAddress?.address || null,
        failedReason: values.failedReason,
        riderNote: values.riderNote,
      };
    }

    if (actionPage === typePage.collo.type) {
      params = {
        width: values.width,
        length: values.length,
        height: values.height,
        weight: values.weight,
        barcode: values.barcode,
      };
    }
    dispatch(doUpdateOrder({ id, ...params }));
  };

  const goRoutePlanDetail = (order, type) => {
    history.push(`/routes/${order.routePlan?.slug}?orderId=${order.id}&type=${type}`);
  };

  const getReviewRemark = (nps) => nps.remarks.mistake || nps.remarks.opinion || 'No comment...';

  const onValuesChange = (changedValues, allValues) => {
    const keysUpdate = Object.keys(changedValues);
    const AllKey = Object.keys(allValues);
    if (!AllKey.includes('locationId')) return;
    if (keysUpdate.includes('locationId')) {
      const location = locations.find((l) => l.id === allValues.locationId);
      const deliveryDate = order.deliveryDate ? dayjs(order.deliveryDate) : undefined;
      if ((allValues.locationId || deliveryDate) && location) {
        if (deliveryDate && location) {
          const day = deliveryDate?.format('dddd');
          form.setFieldValue(
            'pickupTimeWindow',
            `${location[day.toLowerCase() + 'From'] || ''} - ${location[day.toLowerCase() + 'To'] || ''}`,
          );
        } else {
          form.setFieldValue('pickupTimeWindow', undefined);
        }

        form.setFieldValue('pickupAddress', location.address);
        form.setFieldValue('pickupPhone', location.phone);
      } else {
        form.setFieldValue('pickupTimeWindow', undefined);
        form.setFieldValue('pickupAddress', undefined);
        form.setFieldValue('pickupPhone', undefined);
      }
    }
  };

  const onClickCopy = (value) => {
    copy(value);
    messageApi.open({
      type: 'success',
      content: 'Copied',
    });
  };

  const SignatureDisplay = ({ signature }) => {
    if (signature) {
      return (
        <>
          <img
            id="sig"
            src={signature}
            alt="Signature"
            style={{
              maxWidth: '100%',
              background: '#F4F4F4',
              height: '100%',
              width: '100%',
              // '-webkit-filter': 'invert(1)',
              // filter: invert('sig') ? 'invert(1)' : undefined,
            }}
          />
        </>
      );
    } else {
      return;
    }
  };

  const isHandledByVeloyd = useMemo(() => {
    if (isAdmin) {
      return false;
    }
    if (order?.externalIds) {
      if (order.externalIds.some((id) => id.externalType.toLowerCase().includes('veloyd'))) {
        return true;
      }
    }
    if (order?.orderBundle) {
      if (
        order.orderBundle.orders?.some(
          (o) => o.externalIds && o.externalIds.some((id) => id.externalType.toLowerCase().includes('veloyd')),
        )
      ) {
        return true;
      }
    }
    return false;
  }, [order]);

  const handleGetLabel = (orderId, colloId) => {
    (async () => {
      setIsPrintingLabels(true);
      try {
        const response = await getLabel({ id: orderId, colloId });
        dispatch(doGetOrder({ id }));
        const pdfData = response.response.label;
        const pdfBlob = await (await fetch(`data:application/pdf;base64,${pdfData}`)).blob();
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
      } catch (error) {
        console.error('Error while opening label PDF:', error);
        alert('An error occurred while trying to open the label PDF. Please try again later or contact support.');
        setIsPrintingLabels(false);
      }
      setIsPrintingLabels(false);
    })();
  };

  const tntUrl = useMemo(() => {
    if (order?.createdBy === 'Ricoh') {
      // send barcode tnt url for ricoh only
      return `${process.env.REACT_APP_TRACKING_URL}barcode/${order.barcode}`;
    }
    return process.env.REACT_APP_TRACKING_URL + order?.orderId;
  }, [order]);

  const carrierTntUrl = useMemo(
    () => order?.externalIds.find((e) => e.externalType === 'CARRIER_TRACK_TRACE_URL')?.externalId,
    [order],
  );

  const copyTntUrl = (url) => {
    if (url) {
      onClickCopy(url);
      window.open(url);
    }
  };

  const content = (
    <div className="actions">
      <Button type="link" onClick={() => onClickCopy(process.env.REACT_APP_URI + 'orders/' + order.orderId)}>
        <ReactSVG className="icon pr-1" src="/icons/assignment.svg" /> Share Order Details
      </Button>
      <Tooltip title={tntUrl ? '' : 'No track n trace available (yet) for this order'}>
        <Button type="link" onClick={() => copyTntUrl(tntUrl)} disabled={!tntUrl}>
          <ReactSVG className="icon" src="/icons/attachment.svg" /> Share Track and Trace URL
        </Button>
      </Tooltip>
      {user.role === ROLES.ADMIN && !!carrierTntUrl && (
        <Button type="link" onClick={() => copyTntUrl(carrierTntUrl)}>
          <SquareCode color="var(--sparqle-blue)" />
          Carrier TnT URL
          {/* <ReactSVG className="icon" src="/icons/attachment.svg" />  */}
        </Button>
      )}
      <Button
        type="link"
        onClick={() => {
          onClickReportIssue();
          setPopoverVisible(false);
        }}
      >
        <ReactSVG className="icon" src="/icons/report.svg" /> Report Issue
      </Button>

      {order?.status !== 'ADDRESS_NOT_FOUND' && (
        <Button type="link" onClick={() => handleGetLabel(order.orderId)} disabled={isPrintingLabels}>
          <ReactSVG className="icon" src="/icons/label.svg" /> Download Label
        </Button>
      )}
    </div>
  );

  if (!order) return <Spin style={{ width: '100vw', height: '100vh' }} spinning={isFetching}></Spin>;

  const itemsStatus = [
    {
      title: <div className="status expected">EXPECTED</div>,
      description: statusDatetime('EXPECTED'),
    },
  ];

  itemsStatus.push({
    title: <div className="status collected">EXPECTED</div>,
    description: statusDatetime('EXPECTED'),
  });

  itemsStatus.push({
    title: <div className="status in_transit">IN TRANSIT</div>,
    description: statusDatetime('IN_TRANSIT'),
  });

  if (order?.status === ORDER_STATUSES.AWAITING_NEXT_ATTEMPT) {
    itemsStatus.push({
      title: <div className={'status ' + order?.status?.toLowerCase()}>{order?.status?.replace('_', ' ')}</div>,
      description: statusDatetime(ORDER_STATUSES.AWAITING_NEXT_ATTEMPT),
    });
  }

  itemsStatus.push({
    title: <div className="status completed">COMPLETED</div>,
    description: statusDatetime('COMPLETED'),
  });
  if (order?.status === ORDER_STATUSES.CANCELLED) {
    itemsStatus.push({
      title: <div className={'status ' + order?.status?.toLowerCase()}>{order?.status?.replace('_', ' ')}</div>,
      description: statusDatetime('CANCELLED'),
    });
  }

  const onClickCancelOrder = () => {
    dispatch(
      doCancelOrder({
        orderId: order.orderId,
      }),
    );
  };

  return (
    <Spin spinning={isFetching || isPrintingLabels}>
      <div className="OrderDetailPage Content">
        {contextHolder}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BreadCrumb breadCrumbs={breadCrumbs} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
            <Popover
              overlayClassName="share-content-order"
              placement="bottomRight"
              content={<OrderIssues orderId={order?.id} setPopoverIssuesVisible={setPopoverIssuesVisible} />}
              trigger="click"
              open={popoverIssuesVisible} // Bind visibility state to Popover
              onOpenChange={(visible) => setPopoverIssuesVisible(visible)}
            >
              <Badge count={issues?.filter((issue) => issue?.hasSeen === false)?.length}>
                <Button className="ButtonWhite orderButton" size="small" onClick={() => setPopoverIssuesVisible(true)}>
                  <ReactSVG className="icon" src="/icons/issues.svg" style={{ marginRight: '5px' }} />
                  Issues
                </Button>
              </Badge>
            </Popover>
            {([ROLES.ADMIN, ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole) || isAdmin) &&
              (isHandledByVeloyd ? (
                <Tooltip title="Editing is disabled for orders handled externally.">
                  <Button className="ButtonPrimary orderButton" type="primary" size="small" disabled>
                    {' '}
                    Edit <ReactSVG className="icon" src="/icons/edit.svg" />
                  </Button>
                </Tooltip>
              ) : (
                <Button className="ButtonPrimary orderButton" type="primary" size="small" onClick={onClickEditInfo}>
                  Edit
                  <ReactSVG className="icon" src="/icons/edit.svg" />
                </Button>
              ))}
            {isAdminOrMerchant && order?.carrier?.capabilities?.canCancel && (
              <Popconfirm
                title="Cancel order"
                description="Are you sure you want to cancel this order?"
                placement="right"
                onConfirm={onClickCancelOrder}
                okText="Yes"
                cancelText="No"
              >
                <Button className="ButtonCancel orderButton" type="default" size="small">
                  Cancel
                  <CloseOutlined />
                </Button>
              </Popconfirm>
            )}
            <Popover
              overlayClassName="share-content-order"
              placement="bottomLeft"
              content={content}
              trigger="click"
              open={popoverVisible} // Bind visibility state to Popover
              onOpenChange={(visible) => setPopoverVisible(visible)}
            >
              <Button className="ButtonWhite orderButton" size="small" onClick={() => setPopoverVisible(true)}>
                <ReactSVG className="icon upload" src="/icons/share.svg" />
              </Button>
            </Popover>
          </div>
        </div>
        <Row gutter={[24, 24]} style={{ maxHeight: '500px', overflow: 'none' }}>
          <Col span={16}>
            <div className="OrderInfo white border-bottom">
              <div className="OrderInfoHeader">
                <div className="left">
                  {order?.mailbox ? (
                    <Tooltip title="This is a mailbox parcel">
                      {' '}
                      <MailOutlined />{' '}
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                  <div className={order.orderType + ' orderType'}>
                    <ReactSVG className="icon" src={'/icons/' + order.orderType + '.svg'} />{' '}
                    {ORDER_TYPE[order.orderType]}
                  </div>
                  <div className="orderId" onClick={() => onClickCopy(order.orderId)}>
                    {order.orderId}
                    <img src="/icons/file_copy.svg" />
                  </div>
                  {isAdmin && (
                    <>
                      <div style={{ cursor: 'pointer' }} onClick={() => setShowLogsForOrder(order.id)}>
                        <CodeTwoTone />
                      </div>
                      <TraceLogs
                        close={() => setShowLogsForOrder(null)}
                        show={!!showLogsForOrder}
                        preset={{ subject: 'ORDER', subjectId: showLogsForOrder }}
                      />
                    </>
                  )}
                  {isAdminOrMerchant && (order?.notOnInvoice || !order?.firstCarrierScanAt) ? (
                    <Tooltip title="This order is not on the invoice">
                      {' '}
                      <BadgeEuro className="h-4 w-4" />{' '}
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="right">
                  <div className="createdInfo">
                    Created {!!order.createdBy && ' From '}
                    {!!order.createdBy && <b>{order.createdBy}</b>} at{' '}
                    <b>{moment(order.createdAt).format(DAYTIME_FORMAT)}</b>
                  </div>
                </div>
              </div>
              <div className="OrderInfoContent">
                <Row gutter={[24, 16]}>
                  <Col span={6}>
                    <label>Order Ref</label>
                    <div className="order-content hasCopy" onClick={() => onClickCopy(order.orderRef)}>
                      {order.orderRef}
                      <img src="/icons/file_copy.svg" />
                    </div>
                  </Col>
                  <Col span={6}>
                    <label>{order.deliveryEtaDate ? 'Delivery ETA' : 'Delivery Date'}</label>
                    <div className="order-content">
                      {order.deliveryEtaDate
                        ? moment(order.deliveryEtaDate).format(DAY_FORMAT)
                        : order.deliveryDate
                          ? moment(order.deliveryDate).format(DAY_FORMAT)
                          : '-'}
                    </div>
                  </Col>
                  <Col span={6}>
                    <label>Attempts</label>
                    <div className="order-content">{order.attempt}</div>
                  </Col>
                  {/* <Col span={6}>
                    <label>Load</label>
                    <div className="order-content">{order.load}</div>
                  </Col> */}
                  <Col span={6}>
                    <label>Name</label>
                    <div className="order-content">{order.name}</div>
                  </Col>
                  <Col span={6}>
                    <label>Status:</label>
                    <div className="order-content">
                      <span className={'status orders ' + order.status?.toLowerCase()}>
                        {ORDER_STATUSES[order.status]}
                      </span>
                    </div>
                  </Col>
                  <Col span={6}>
                    <label>Size (LxWxH)</label>
                    <div className="order-content">
                      {order.width ? order.width + ' x ' : ''} {order.length ? order.length + ' x ' : ''} {order.height}
                    </div>
                  </Col>
                  <Col span={6}>
                    <label>Weight</label>
                    <div className="order-content">{order.weight}</div>
                  </Col>

                  <Col span={6}>
                    <label>Barcode</label>
                    <Tooltip title={order.barcode}>
                      <div className="order-content truncate">{order.barcode}</div>
                    </Tooltip>
                  </Col>
                </Row>
              </div>
            </div>
            {/* <div className="statuses-log white border-bottom">
              {userRole === ROLES.ADMIN && (
                <div className="header">
                  <>
                    {isHandledByVeloyd ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center', // Aligns vertically in the center
                          gap: '10px', // Adds space between image and label
                        }}
                      >
                        <img
                          src="/icons/veloyd.png"
                          alt="Veloyd Integration"
                          style={{ width: '20px', height: '20px' }}
                        />
                        <label>This order is handled through Veloyd.</label>
                      </div>
                    ) : (
                      <div className="Info Rider">
                        <label>Rider: </label>
                        <div className="Text">{order.rider?.user?.name || 'Unassigned'}</div>
                      </div>
                    )}
                  </>
                </div>
              )}
              <div className="statuses">
                <Steps current={getProgressStatus(order.status)} labelPlacement="vertical" items={itemsStatus} />
              </div>
            </div> */}
            <div className="collo white border-bottom">
              <b>
                Bundled orders ({order.orderBundle?.orders?.length > 1 ? order.orderBundle?.orders?.length - 1 : 0})
              </b>
              <Space direction="horizontal" size={16} className="grid grid-cols-4 gap-4">
                {order?.orderBundle?.orders?.length > 0 &&
                  [
                    // order,
                    ...order.orderBundle.orders,
                    // .filter((or) => or.orderId !== order.orderId),
                  ].map((o) => (
                    <Button
                      key={o.id}
                      type={o.orderId !== order.orderId ? 'default' : 'primary'}
                      href={o.orderId !== order.orderId ? o.orderId : null}
                    >
                      <p>{o.orderId}</p>
                    </Button>
                  ))}
              </Space>
            </div>
            <div className="super-note white">
              {userRole === ROLES.ADMIN && <SupportNote userRole={userRole} isAdmin={isAdmin} order={order} />}
            </div>
          </Col>
          <Col span={8}>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div className="TimeLine">
                <div>
                  <h4 style={{ margin: '0px' }}>Timeline</h4>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: '490px' }}>
                  <HistoryLogs
                    order={order}
                    tntSentEvents={order?.messagingLogs.filter((log) => log.lockedUntil !== null)}
                  />
                </div>
              </div>
              <div className="Review">
                <div>
                  <h4 style={{ margin: '0px', display: 'flex', justifyContent: 'space-between' }}>
                    <div>Review</div>
                    <Rate disabled value={order.NPS?.score} />
                  </h4>

                  <div style={{ marginTop: '4px', color: 'lightGray' }}>
                    {order.NPS?.score
                      ? `Review completed on ${format(order.NPS.updatedAt, 'dd/MM/yyy HH:mm')}`
                      : `No review yet`}
                  </div>
                </div>
                {order.NPS?.score && (
                  <div>
                    <Divider type="vertical" />
                    {getReviewRemark(order.NPS)}
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="PartnerInfo Pickup white">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div className="header left">
                    <div className="img">
                      {!order.isReturn && order?.location?.company?.logoUrl && (
                        <img
                          src={order?.location.company.logoThumbUrl || order?.location.company.logoUrl}
                          alt={order?.location?.company?.name}
                        />
                      )}
                    </div>
                    <div className="text">
                      <h5>{order.isReturn ? order?.pickupName || '-' : order?.location?.name || '-'}</h5>
                      <span>{!order.isReturn ? order?.location?.locationId || '-' : order.deliveryEmail}</span>
                    </div>
                    <div>
                      <Tag color={`#${SPARQLE_DARK_BLUE}`}>{order.isReturn ? 'Return order' : ''}</Tag>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="actions header right">
                    {((!order.routePlanId && [ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole)) ||
                      userRole === ROLES.ADMIN) &&
                      (isHandledByVeloyd ? (
                        <Tooltip title="Editing is disabled for orders handled externally.">
                          <Button type="link" size="small" disabled>
                            {' '}
                            Edit <ReactSVG className="icon" src="/icons/edit.svg" />
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button type="link" size="small" onClick={onClickEditPickup}>
                          Edit
                          <ReactSVG className="icon" src="/icons/edit.svg" />
                        </Button>
                      ))}
                    {order.routePlanId && userRole === ROLES.ADMIN && (
                      <Button
                        type="link"
                        size="small"
                        disabled={!order.routePlanId}
                        onClick={() => goRoutePlanDetail(order, STEP_TYPE.pickup)}
                      >
                        View on Route Plan
                        <ReactSVG className="icon" src="/icons/routePlan.svg" />
                      </Button>
                    )}
                  </div>
                </Col>
                <Col span={24}>
                  <label>Address</label>
                  <div className="order-content hasCopy" onClick={() => onClickCopy(order.pickupAddress)}>
                    {order.pickupAddress}
                    <img src="/icons/file_copy.svg" />
                  </div>
                </Col>
                <Col span={8}>
                  <label>Phone Number</label>
                  <div className="order-content"> {order.pickupPhone}</div>
                </Col>
                <Col span={8}>
                  {userRole === ROLES.ADMIN && (
                    <>
                      <label>Pickup Time Window</label>
                      <div className="order-content">
                        {order.pickupTimeStart || ''} - {order.pickupTimeEnd || ''}
                      </div>
                    </>
                  )}
                </Col>
                <Col span={8}>
                  {userRole === ROLES.ADMIN && (
                    <div>
                      <label>Duration</label>
                      <div className="order-content">
                        {order.pickupDuration
                          ? order.pickupDuration + (order.pickupDuration > 1 ? ' minutes' : ' minute')
                          : ''}
                      </div>
                    </div>
                  )}
                </Col>
                <Col span={8}>
                  <label>Note</label>
                  <div className="order-content">{order.pickupNote || '-'}</div>
                </Col>
              </Row>
            </div>
          </Col>
          <Col span={12}>
            <div className="PartnerInfo Delivery white">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div className="header left">
                    <div className="text">
                      <h5>{order?.deliveryName || '-'}</h5>
                      <span>{order?.deliveryEmail || '-'}</span>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="actions header right">
                    {((!order.routePlanId && [ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole)) ||
                      userRole === ROLES.ADMIN) &&
                      (isHandledByVeloyd ? (
                        <Tooltip>
                          <Tooltip title="Editing is disabled for orders handled externally.">
                            <Button type="link" size="small" disabled>
                              {' '}
                              Edit <ReactSVG className="icon" src="/icons/edit.svg" />
                            </Button>
                          </Tooltip>
                        </Tooltip>
                      ) : (
                        <Button type="link" size="small" onClick={onClickEditDelivery}>
                          Edit
                          <ReactSVG className="icon" src="/icons/edit.svg" />
                        </Button>
                      ))}
                    {order.routePlanId && userRole === ROLES.ADMIN && (
                      <Button
                        type="link"
                        size="small"
                        disabled={!order.routePlanId}
                        onClick={() => goRoutePlanDetail(order, STEP_TYPE.delivery)}
                      >
                        View on Route Plan
                        <ReactSVG className="icon" src="/icons/routePlan.svg" />
                      </Button>
                    )}
                  </div>
                </Col>
                <Col span={24}>
                  <label>Address</label>
                  <div className="order-content hasCopy" onClick={() => onClickCopy(order.deliveryAddress)}>
                    {order.deliveryAddress}
                    <img src="/icons/file_copy.svg" />
                  </div>
                </Col>
                <Col span={8}>
                  <label>Phone Number</label>
                  <div className="order-content">{order.deliveryPhone}</div>
                </Col>
                <Col span={8}>
                  <label>Delivery Time Window</label>
                  <div className="order-content">
                    {order.deliveryTimeStart || ''} - {order.deliveryTimeEnd || ''}
                  </div>
                </Col>
                <Col span={8}>
                  {userRole === ROLES.ADMIN && (
                    <div>
                      <label>Duration</label>
                      <div className="order-content">
                        {order.deliveryDuration
                          ? order.deliveryDuration + (order.deliveryDuration > 1 ? ' minutes' : ' minute')
                          : '-'}
                      </div>
                    </div>
                  )}
                </Col>
                <Col span={8}>
                  <label>Note</label>
                  <div className="order-content">{order.deliveryNote || '-'}</div>
                </Col>
              </Row>
            </div>
          </Col>
          <Col span={12}>
            <div className="wrapper-map">
              <DefaultMapbox markers={markers} className="mapbox-order-detail" />
            </div>
          </Col>
          <Col span={12}>
            <div className="PartnerInfo DeliveryDetail white">
              <Row>
                <Col span={24}>
                  <div className="border-bottom ">
                    <Row gutter={[24, 16]}>
                      <Col span={12}>
                        <div className="header left">
                          <div className="text">
                            <h5>Delivery Status</h5>
                          </div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div className="actions header right">
                          {((!order.routePlanId && [ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole)) ||
                            userRole === ROLES.ADMIN) && (
                            <Tooltip
                              title={isHandledByVeloyd ? 'Editing is limited for orders handled externally.' : ''}
                            >
                              <Button type="link" size="small" onClick={onClickEditDeliveryStatus}>
                                {' '}
                                Edit <ReactSVG className="icon" src="/icons/edit.svg" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </Col>
                    </Row>
                    {order?.packageWas && (
                      <p>
                        Order was: <b>{order?.packageWas}</b>
                      </p>
                    )}
                    {order?.neighbourAddress && (
                      <p>
                        Neighbour address: <b>{order?.neighbourAddress}</b>
                      </p>
                    )}
                    {order?.status === ORDER_STATUSES.AWAITING_NEXT_ATTEMPT && order?.failedReason && (
                      <p>
                        Failed reason: <b>{order?.failedReason}</b>
                      </p>
                    )}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="wrapper">
                    <div className="title">POD</div>
                    <ImageGallery pods={order?.pods} />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="wrapper border-left">
                    <div className="title">Signature</div>
                    <div style={{ marginTop: '16px', background: '#F4F4F4', height: '325px', width: '100%' }}>
                      <SignatureDisplay signature={order?.signatureImage || undefined} />
                    </div>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="wrapper border-top">
                    <div className="title">Rider Note</div>
                    <div className="content">{order?.riderNote || '-'}</div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
      <Modal
        width={1128}
        closeIcon={false}
        title={titleModal || 'Edit Order Info'}
        open={isModalOpen}
        forceRender
        onCancel={cancelModal}
      >
        <Spin spinning={isFetching}>
          <div className="OrderDetailModal">
            <Form
              form={form}
              scrollToFirstError
              layout="vertical"
              requiredMark={false}
              colon={false}
              className="FormContent"
              onFinish={onFinishForm}
              onValuesChange={onValuesChange}
            >
              {actionPage === typePage.order.type && (
                <OrderForm
                  types={types}
                  setTypes={setTypes}
                  order={order}
                  limitedFields={[ROLES.COMPANY_OWNER, ROLES.LOCATION_USER].includes(userRole)}
                />
              )}

              {actionPage === typePage.collo.type && (
                // Collo
                <ColloForm form={form} name="singleCollo" isNew={false} />
              )}

              {((!order.isReturn && actionPage === typePage.pickup.type) ||
                (order.isReturn && actionPage === typePage.delivery.type)) && (
                <PickupForm
                  //locations={locations}
                  //companies={companies}
                  form={form}
                  companyId={order.location?.companyId}
                  isReturn={order.isReturn}
                />
              )}
              {((!order.isReturn && actionPage === typePage.delivery.type) ||
                (order.isReturn && actionPage === typePage.pickup.type)) && (
                <DeliveryForm form={form} isReturn={order.isReturn} />
              )}
              {actionPage === typePage.deliveryStatus.type && (
                <DeliveryStatusForm form={form} order={order} isHandledByVeloyd={isHandledByVeloyd} />
              )}
              <div className="FormFooter">
                <Button className="ButtonCancel" onClick={cancelModal}>
                  Cancel
                </Button>
                <Button className="CreateButton ButtonPrimary" type="primary" htmlType="submit">
                  Update
                  <ReactSVG className="icon" src="/icons/done.svg" />
                </Button>
              </div>
            </Form>
          </div>
        </Spin>
      </Modal>
      <Modal width={552} title="History Logs" open={isLogsModalOpen} onCancel={cancelLogsModal}>
        <HistoryLogs order={order} />
        <div className="FormFooter">
          <Button className="ButtonCancel" onClick={cancelLogsModal}>
            Cancel
          </Button>
        </div>
      </Modal>
      <Modal width={552} title="Report an Issue" open={isReportIssueModalOpen} onCancel={cancelReportIssueModal}>
        <ReportIssueForm
          issue={{
            orderId: order?.id || 0,
            carrierName: order?.carrier?.carrierName || '',
            companyName: order?.location?.company?.name || '',
          }}
          userId={user?.id}
          onCancel={cancelReportIssueModal}
          onSuccess={() => {
            setIsReportIssueModalOpen(false);
          }}
        />
      </Modal>
      <Modal width={752} title="Issue" open={isIssueModalOpen} onCancel={cancelIssueModal}>
        {/* <Issue issueId={selectedIssueId}  userId={user?.id} onCancel={cancelIssueModal} /> */}
        <div style={{ height: '700px' }}>
          <IssueChat selectedIssue={issue} userId={user?.id} />
        </div>
      </Modal>
    </Spin>
  );
};

export default withPage(OrderDetailPage);

const ImageGallery = ({ pods }) => {
  if (pods) {
    const images = pods.map((pod) => `${process.env.REACT_APP_S3_URL}${pod}`);
    const extraImages = images.length - 4;
    const displayImages = extraImages > 0 ? images.slice(0, 3) : images.slice(0, 4); // Only take first 3 images if extraImages > 0, else take first 4 images for display

    return (
      <Image.PreviewGroup>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '16px' }}>
          {displayImages?.map((src, index) => (
            <Image
              key={index}
              src={src}
              style={{ width: '100%', display: 'block' }}
              preview={{ visible: false }}
              onClick={(e) => e.currentTarget.parentElement.nextSibling.click()}
            />
          ))}
          {extraImages > 0 && (
            <div style={{ position: 'relative' }}>
              <Image
                src={images[3]} // Use the 4th image to represent the extra images
                style={{ width: '100%', display: 'block', filter: 'brightness(50%)' }}
                preview={{ visible: false }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  color: 'white',
                  fontSize: '28px',
                  fontWeight: 'bold',
                }}
              >
                +{extraImages}
              </div>
            </div>
          )}
        </div>
        {/* Hidden element for triggering the preview modal */}
        <div style={{ display: 'none' }}>
          {extraImages > 0 &&
            images
              .slice(4)
              .map((src, index) => <Image key={index} src={src} style={{ width: '100%', display: 'block' }} />)}
        </div>
      </Image.PreviewGroup>
    );
  } else {
    return;
  }
};
